import { computed, ref, watch } from "vue";
import Swal from "sweetalert2";
import { useI18n } from "vue-i18n";

import categoryService, { type Category } from "../services/category";
import { buildAssetUrl } from "../utils/settings";

type CategoryForm = Omit<Category, "image"> & {
  image?: string | File;
};

type CategorySaveError = {
  response?: {
    status?: number;
    data?: {
      errors?: Record<string, string[]>;
      message?: string;
    };
  };
};

export function useCategory() {
  const { t } = useI18n();
  const categories = ref<Category[]>([]);
  const searchQuery = ref("");
  const showModal = ref(false);
  const selectedCategory = ref<Category | null>(null);
  const loading = ref(false);
  const errors = ref<Record<string, string[]>>({});
  const previewUrl = ref("");

  const form = ref<CategoryForm>({
    code: "",
    name: "",
    description: "",
    status: 1,
  });

  const fetchCategories = async () => {
    loading.value = true;
    try {
      categories.value = await categoryService.getCategories();
    } catch (error) {
      console.error("Failed to fetch categories:", error);
      Swal.fire({
        icon: "error",
        title: t("category_page.load_failed"),
        text: t("category_page.load_failed_text"),
      });
    } finally {
      loading.value = false;
    }
  };

  const filteredCategories = computed(() => {
    const query = searchQuery.value.trim().toLowerCase();
    if (!query) return categories.value;

    return categories.value.filter((category) =>
      [category.code, category.name, category.description]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query)),
    );
  });

  const pageSizeOptions = ref(["10", "25", "50", "100", "1000"]);

  const paginationConfig = computed(() => ({
    showSizeChanger: true,
    pageSizeOptions: pageSizeOptions.value,
    showTotal: (total: number, range: [number, number]) =>
      t("category_page.pagination_total", {
        start: range[0],
        end: range[1],
        total,
      }),
    defaultPageSize: 10,
    locale: { items_per_page: t("category_page.items_per_page") },
  }));

  watch(
    filteredCategories,
    (newValue) => {
      const totalCount = newValue.length > 0 ? newValue.length : 1000;
      pageSizeOptions.value = ["10", "25", "50", "100", String(totalCount)];
    },
    { immediate: true },
  );

  const resetForm = () => {
    form.value = {
      code: "",
      name: "",
      description: "",
      status: 1,
    };
    errors.value = {};
    previewUrl.value = "";
  };

  const openCreateModal = () => {
    selectedCategory.value = null;
    resetForm();
    showModal.value = true;
  };

  const openEditModal = (category: Category) => {
    selectedCategory.value = category;
    errors.value = {};
    form.value = {
      id: category.id,
      code: category.code || "",
      name: category.name || "",
      description: category.description || "",
      status: Number(category.status ?? 1),
      image: category.image,
    };
    previewUrl.value =
      typeof category.image === "string" ? buildAssetUrl(category.image) : "";
    showModal.value = true;
  };

  const closeModal = () => {
    showModal.value = false;
    previewUrl.value = "";
    errors.value = {};
  };

  const updateField = (field: keyof CategoryForm, value: string | number | undefined) => {
    form.value = {
      ...form.value,
      [field]: value,
    };
  };

  const handleFileUpload = (event: Event) => {
    const target = event.target as HTMLInputElement | null;
    const file = target?.files?.[0] ?? null;

    if (!file) {
      if (!selectedCategory.value?.image) {
        previewUrl.value = "";
      }
      return;
    }

    form.value.image = file;
    previewUrl.value = URL.createObjectURL(file);
  };

  const removeImage = () => {
    form.value.image = undefined;
    previewUrl.value = "";
  };

  const generateCategoryCode = async () => {
    try {
      if (!categories.value.length) {
        await fetchCategories();
      }

      const nextNumber =
        categories.value
          .map((item) => {
            const match = String(item.code || "").match(/(\d+)\s*$/);
            return match ? Number(match[1]) : 0;
          })
          .reduce((max, current) => Math.max(max, current), 0) + 1;

      form.value.code = `CAT${String(nextNumber).padStart(3, "0")}`;
    } catch (error) {
      console.error("Failed to generate category code:", error);
      Swal.fire({
        icon: "error",
        title: t("category_page.generate_failed"),
        text: t("category_page.generate_failed_text"),
      });
    }
  };

  const saveCategory = async () => {
    loading.value = true;
    errors.value = {};

    try {
      const payload = new FormData();
      payload.append("code", form.value.code || "");
      payload.append("name", form.value.name || "");
      payload.append("description", form.value.description || "");
      payload.append("status", String(Number(form.value.status ?? 1)));

      if (form.value.image instanceof File) {
        payload.append("image", form.value.image);
      }

      if (selectedCategory.value?.id) {
        await categoryService.updateCategory(selectedCategory.value.id, payload);
      } else {
        await categoryService.createCategory(payload);
      }

      await fetchCategories();
      await Swal.fire({
        icon: "success",
        title: selectedCategory.value
          ? t("category_page.updated_title")
          : t("category_page.created_title"),
        timer: 1400,
        showConfirmButton: false,
      });
      closeModal();
    } catch (error: unknown) {
      const requestError = error as CategorySaveError;
      console.error(error);
      if (requestError.response?.status === 422) {
        errors.value = requestError.response.data?.errors || {};
      } else {
        Swal.fire({
          icon: "error",
          title: t("category_page.save_failed"),
          text: requestError.response?.data?.message || t("category_page.save_failed_text"),
        });
      }
    } finally {
      loading.value = false;
    }
  };

  const deleteCategory = async (category: Category) => {
    if (!category?.id) return;

    const result = await Swal.fire({
      title: t("category_page.delete_title"),
      text: t("category_page.delete_text", { name: category.name }),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: t("category_page.delete_confirm"),
      cancelButtonText: t("common.cancel"),
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    try {
      Swal.fire({
        title: t("category_page.deleting_title"),
        text: t("category_page.deleting_text"),
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      await categoryService.deleteCategory(category.id);
      await fetchCategories();

      Swal.fire({
        icon: "success",
        title: t("category_page.deleted_title"),
        text: t("category_page.deleted_text", { name: category.name }),
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error(error);
      Swal.fire({
        icon: "error",
        title: t("category_page.delete_failed"),
        text: t("category_page.delete_failed_text"),
      });
    }
  };

  return {
    searchQuery,
    filteredCategories,
    showModal,
    selectedCategory,
    form,
    errors,
    loading,
    previewUrl,
    paginationConfig,
    fetchCategories,
    openCreateModal,
    openEditModal,
    closeModal,
    updateField,
    handleFileUpload,
    removeImage,
    generateCategoryCode,
    saveCategory,
    deleteCategory,
  };
}
